import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SessionManager } from '@/components/organisms/SessionManager';
import { MapPage } from '@/components/pages/MapPage';
import { ROUTES } from '@/routes/config';
import AuthLayout from '@/routes/layouts/AuthLayout';
import { useAuthStore } from '@/stores/slices/useAuthStore';
import { adaptUserToAuthUser, createSetIsLoggedInAdapter } from '@/utils/auth/userAdapter';
import Home from '../pages/Home';

/**
 * AppRoutes
 * Defines the application's route structure using Layouts and Outlets.
 */
const AppRoutes: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const loading = useAuthStore((state) => state.loading);
  const setLoading = useAuthStore((state) => state.setLoading);
  const isInitializing = useAuthStore((state) => state.isInitializing);
  const getToken = useAuthStore((state) => state.getToken);

  const authUser = React.useMemo(() => adaptUserToAuthUser(user), [user]);

  const setIsLoggedIn = React.useMemo(() => createSetIsLoggedInAdapter(setUser), [setUser]);

  // Memoize MapPage to prevent re-renders
  const mapViewElement = React.useMemo(() => (getToken ? <MapPage getToken={getToken} /> : null), [getToken]);

  const LoadingFallback = () => (
    <div className="flex items-center justify-center h-screen bg-[#0a0e1a] text-white">Loading...</div>
  );

  return (
    <>
      <SessionManager />

      {isInitializing ? (
        <LoadingFallback />
      ) : (
        <Routes>
          {/* Public routes */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <Home isLoggedIn={authUser} setIsLoggedIn={setIsLoggedIn} loading={loading} setLoading={setLoading} />
            }
          />
          <Route
            path={ROUTES.SIGNUP}
            element={
              <Home isLoggedIn={authUser} setIsLoggedIn={setIsLoggedIn} loading={loading} setLoading={setLoading} />
            }
          />

          {/* Root redirect */}
          <Route
            path="/"
            element={user ? <Navigate replace to={ROUTES.HOME} /> : <Navigate replace to={ROUTES.LOGIN} />}
          />

          {/* Protected routes wrapped in AuthLayout */}
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.HOME} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.VESSELS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.VESSEL_DETAILS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.FLEET_MANAGER} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.SATELLITES} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.TASKING} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.DRONES} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.RULES_ALERTS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.SENSOR_FUSION} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.SENSOR_FUSION_RESULTS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.SENSOR_FUSION_DEMO} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.GEOFENCES} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.SANCTIONS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.INCIDENTS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.ILLEGAL_FISHING} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.REPORTS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.INSIGHTS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.ACCOUNT} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.RULE_DETAILS} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.NETWORK} element={mapViewElement || <LoadingFallback />} />
            <Route path={ROUTES.AREA_TRAFFIC} element={mapViewElement || <LoadingFallback />} />
          </Route>

          {/* Catch-all */}
          <Route
            path="*"
            element={user ? <Navigate replace to={ROUTES.HOME} /> : <Navigate replace to={ROUTES.LOGIN} />}
          />
        </Routes>
      )}
    </>
  );
};

export default AppRoutes;
